import React, { useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const SignatureField = ({
    fieldName,
    label = "",
    formData,
    isEditing,
    isDisabled,
    signatures,
    canvasRefs,
    invalidFields,
    updateSignatureData,
    clearSignature,
    toggleEditMode,
    isNext
}) => {
    const localRef = canvasRefs[fieldName];

    useEffect(() => {
        const canvasEl = localRef?.current?.getCanvas?.();
        if (!canvasEl) return;

        // Prevent touch gestures from scrolling or zooming the page
        const preventTouch = (e) => e.preventDefault();
        canvasEl.addEventListener("touchstart", preventTouch, { passive: false });
        canvasEl.addEventListener("touchmove", preventTouch, { passive: false });

        // Improve drawing on touch devices
        canvasEl.style.touchAction = 'none';

        return () => {
            canvasEl.removeEventListener("touchstart", preventTouch);
            canvasEl.removeEventListener("touchmove", preventTouch);
        };
    }, [localRef]);

    const hasError = invalidFields.includes(label) && !formData[fieldName];

    return (
        <div className="relative">
            <label className="block text-base font-medium text-black mb-1">
                {label}
            </label>

            {formData[fieldName] && !isEditing[fieldName] ? (
                <img
                    src={signatures[fieldName]}
                    alt="Signature"
                    loading="eager"
                    style={{
                        width: '300px',
                        height: '100px',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        borderRadius: '0.375rem',
                        border: '2px solid #4B5563', // darker gray for better contrast
                        objectFit: 'contain',
                        display: 'block',
                        boxShadow: '0 0 5px rgba(0,0,0,0.2)', // subtle highlight
                    }}
                />
            ) : (
                <div className="relative" style={{ width: window.innerWidth < 768 ? 300 : 450, height: 160 }}>
                    <SignatureCanvas
                        ref={localRef}
                        backgroundColor="white"
                        penColor="#1a1a1a"
                        disabled={isDisabled}
                        canvasProps={{
                            width: window.innerWidth < 768 ? 300 : 450,
                            height: 160,
                            className: `rounded-lg focus:outline-none focus:ring-4 focus:ring-offset-2
                ${hasError
                                    ? 'border-[3px] border-dashed border-red-700 focus:ring-red-500'
                                    : 'border-[3px] border-dashed border-blue-900 focus:ring-blue-600'
                                }`,
                            style: {
                                backgroundColor: 'white',
                                backgroundImage: `
        repeating-linear-gradient(0deg, #c8c8c8, #c8c8c8 1px, transparent 1px, transparent 20px),
        repeating-linear-gradient(90deg, #c8c8c8, #c8c8c8 1px, transparent 1px, transparent 20px)
      `,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            },
                        }}
                    />

                    {!isDisabled && !formData[fieldName] && (
                        <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                   flex flex-col items-center gap-1 pointer-events-none select-none">
                            {/* Pen icon */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-7 h-7 text-blue-400"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round"
                                    d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.5 19.213l-4.5 1.125 1.125-4.5L16.862 3.487z" />
                            </svg>
                            <span className="text-gray-600 text-base font-semibold tracking-wide">
                                Sign here
                            </span>
                        </span>
                    )}
                </div>
            )}

            {!isNext && (
                <div className="flex space-x-4 mt-2">
                    {!formData[fieldName] && (
                        <button
                            disabled={isDisabled}
                            type="button"
                            onClick={() => updateSignatureData(fieldName)}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            <i className="fas fa-save mr-2"></i>
                            Save
                        </button>
                    )}

                    <button
                        disabled={isDisabled}
                        type="button"
                        onClick={() => {
                            toggleEditMode(fieldName, true);
                            clearSignature(fieldName);
                        }}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                        <i className="fas fa-eraser mr-2"></i>
                        Clear
                    </button>
                </div>
            )}
        </div>
    );
};

export default SignatureField;